import React, { useState } from 'react';
import { doc, updateDoc, getDoc, setDoc, runTransaction } from 'firebase/firestore';
import { ArrowLeft, Award, Lock, Unlock, UserX } from 'lucide-react';
import { db, APP_ID_INTERNO } from '../firebase';
import './Phase2Bracket.css';

export default function Phase2Bracket({ tournament, role }) {
    const [localScores, setLocalScores] = useState({});

    const toggleDisqualify = async (playerId) => {
        if (role !== 'admin') return;
        const playerObj = tournament.players.find(p => p.id === playerId);
        const isDQ = !!playerObj?.isDisqualified;
        if (!window.confirm(`¿Seguro que quieres ${isDQ ? 'reintegrar' : 'descalificar'} a "${playerObj?.name}"?`)) return;

        try {
            const tRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id);
            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(tRef);
                if (!sfDoc.exists()) return;
                const currentPlayers = sfDoc.data().players || [];
                const updatedPlayers = currentPlayers.map(p => 
                    p.id === playerId ? { ...p, isDisqualified: !isDQ } : p
                );
                transaction.update(tRef, { players: updatedPlayers });
            });
        } catch (e) {
            console.error("Error al descalificar/reintegrar jugador:", e);
        }
    };

    const handleLocalScoreChange = (ri, mi, field, val) => {
        const key = `${ri}_${mi}`;
        const match = tournament.phase2Bracket[ri]?.matches[mi];
        const existingP1 = localScores[key]?.p1Score ?? match?.p1Score ?? '';
        const existingP2 = localScores[key]?.p2Score ?? match?.p2Score ?? '';
        setLocalScores(prev => ({
            ...prev,
            [key]: {
                p1Score: field === 'p1Score' ? val : existingP1,
                p2Score: field === 'p2Score' ? val : existingP2
            }
        }));
    };

    const toggleBLock = async (ri, mi) => {
        if (role !== 'admin') return;
        const key = `${ri}_${mi}`;
        const match = tournament.phase2Bracket[ri]?.matches[mi];
        if (!match) return;

        const isCurrentlyLocked = !!match.isLocked;
        const p1Val = localScores[key]?.p1Score ?? match.p1Score ?? '';
        const p2Val = localScores[key]?.p2Score ?? match.p2Score ?? '';

        try {
            const tRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id);
            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(tRef);
                if (!sfDoc.exists()) return;
                let b = JSON.parse(JSON.stringify(sfDoc.data().phase2Bracket || []));
                if (!b[ri] || !b[ri].matches[mi]) return;
                
                let m = b[ri].matches[mi];
                
                if (isCurrentlyLocked) {
                    m.isLocked = false;
                } else {
                    m.p1Score = p1Val;
                    m.p2Score = p2Val;
                    m.isLocked = true;

                    if (m.p1Score !== '' && m.p2Score !== '' && m.p1Score !== m.p2Score) {
                        m.status = 'completed';
                        let win = Number(m.p1Score) > Number(m.p2Score) ? m.p1 : m.p2;
                        let los = Number(m.p1Score) < Number(m.p2Score) ? m.p1 : m.p2;
                        
                        if (ri + 1 < b.length && !b[ri+1].matches[0].isThird) {
                            let nextM = b[ri+1].matches[Math.floor(mi/2)];
                            if (mi % 2 === 0) nextM.p1 = win; else nextM.p2 = win;
                            
                            if (b.find(r => r.name === '3er Puesto')) {
                                let third = b.find(r => r.name === '3er Puesto').matches[0];
                                if (mi === 0 && ri === b.length - 3) third.p1 = los; 
                                else if (mi === 1 && ri === b.length - 3) third.p2 = los;
                            }
                        }
                    } else {
                        m.status = 'pending';
                    }
                }
                transaction.update(tRef, { phase2Bracket: b });
            });

            setLocalScores(prev => {
                const updated = { ...prev };
                delete updated[key];
                return updated;
            });
        } catch (e) {
            console.error("Error al cambiar bloqueo/guardar bracket en transacción:", e);
        }
    };

    const calculateLeaguePoints = async () => {
        if (role !== 'admin') return;
        if (!window.confirm("¿Estás seguro de finalizar el torneo? Se generará la tabla oficial de Puntos de Liga.")) return;

        let results = {};
        
        tournament.players.forEach(p => {
            results[p.id] = { id: p.id, name: p.name, part: 1, sWins: 0, qual: 0, bWins: 0, runUp: 0, champ: 0, total: 0 };
        });

        tournament.phase1Matches.forEach(m => {
            if (m.isKubey) {
                let hId = m.p1 === 'kubey' ? m.p2 : m.p1;
                const realMatches = tournament.phase1Matches.filter(rm => !rm.isKubey && rm.status === 'completed' && (rm.p1 === hId || rm.p2 === hId));
                
                if (realMatches.length > 0) {
                    let totalPf = 0, totalPa = 0;
                    realMatches.forEach(rm => {
                        if (rm.p1 === hId) { totalPf += Number(rm.p1Score); totalPa += Number(rm.p2Score); }
                        else { totalPf += Number(rm.p2Score); totalPa += Number(rm.p1Score); }
                    });
                    
                    let avgPf = totalPf / realMatches.length;
                    let avgPa = totalPa / realMatches.length;
                    
                    if (avgPf > avgPa && results[hId]) {
                        results[hId].sWins++;
                    }
                }
            } else {
                if (m.status !== 'completed') return;
                let s1 = Number(m.p1Score), s2 = Number(m.p2Score);
                if (s1 > s2 && results[m.p1]) results[m.p1].sWins++;
                if (s2 > s1 && results[m.p2]) results[m.p2].sWins++;
            }
        });

        if (tournament.phase2Bracket && tournament.phase2Bracket.length > 0) {
            tournament.phase2Bracket[0].matches.forEach(m => {
                if (m.p1 && results[m.p1]) results[m.p1].qual = 1;
                if (m.p2 && results[m.p2]) results[m.p2].qual = 1;
            });

            tournament.phase2Bracket.forEach(round => {
                let isFinal = round.matches.length === 1 && !round.matches[0].isThird;
                
                round.matches.forEach(m => {
                    if (m.status !== 'completed') return;
                    let s1 = Number(m.p1Score), s2 = Number(m.p2Score);
                    if (s1 === s2) return; 
                    
                    let winner = s1 > s2 ? m.p1 : m.p2;
                    let loser = s1 > s2 ? m.p2 : m.p1;

                    if (winner && results[winner]) results[winner].bWins++;
                    
                    if (isFinal) {
                        if (winner && results[winner]) results[winner].champ = 1; 
                        if (loser && results[loser]) results[loser].runUp = 1; 
                    }
                });
            });
        }

        let finalArray = Object.values(results).map(r => {
            r.total = r.part + (r.sWins * 1) + (r.qual * 1) + (r.bWins * 2) + (r.runUp * 2) + (r.champ * 5);
            return r;
        });

        finalArray.sort((a, b) => b.total - a.total);

        // RESTAR BONOS SI NO SE HA HECHO YA EN ESTE TORNEO
        let bonosDeducted = tournament.bonosDeducted || false;
        if (!bonosDeducted) {
            try {
                const bpRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'ajustes', 'puntosBase');
                const bpSnap = await getDoc(bpRef);
                if (bpSnap.exists()) {
                    let bpData = bpSnap.data();
                    let changed = false;
                    
                    tournament.players.forEach(p => {
                        const pKey = p.name.trim().toUpperCase();
                        if (bpData[pKey] && bpData[pKey].bonos > 0) {
                            bpData[pKey].bonos -= 1;
                            changed = true;
                        }
                    });
                    
                    if (changed) {
                        await setDoc(bpRef, bpData);
                    }
                }
                bonosDeducted = true;
            } catch (error) {
                console.error("Error al restar bonos:", error);
            }
        }

        await updateDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id), { 
            phase: 'completed',
            leagueResults: finalArray,
            bonosDeducted
        });
    };

    return (
        <div className="bracket-container">
            {role === 'admin' && (
                <div className="bracket-actions">
                    <button onClick={async () => { if(window.confirm("¿Volver al suizo? Se perderá el bracket actual.")) await updateDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id), { phase: 'phase1' }); }} className="bracket-btn-back">
                        <ArrowLeft className="w-4 h-4"/> Regresar al Suizo
                    </button>
                    <button onClick={calculateLeaguePoints} className="bracket-btn-finish">
                        Finalizar Torneo y Ver Puntos <Award className="w-5 h-5"/>
                    </button>
                </div>
            )}
            
            <div className="bracket-tree">
                {tournament.phase2Bracket.map((r, ri) => (
                    <div key={ri} className="bracket-round">
                        <h3 className="bracket-round-title">
                            {r.name}
                        </h3>
                        <div className="bracket-matches-list">
                            {r.matches.map((m, mi) => {
                                const p1Obj = tournament.players.find(p=>p.id===m.p1);
                                const p2Obj = tournament.players.find(p=>p.id===m.p2);
                                return (
                                    <div key={m.id} className={`bracket-match-card ${m.status === 'completed' ? 'is-completed' : ''}`}>
                                        <div className="bracket-player-row">
                                            <div className={`bracket-player-name ${m.status === 'completed' && Number(m.p1Score) > Number(m.p2Score) ? 'is-winner' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span>{p1Obj?.name || 'POR DEFINIR'}</span>
                                                {p1Obj?.isDisqualified && <span className="dq-badge">(DQ)</span>}
                                                {role === 'admin' && p1Obj && (
                                                    <button 
                                                        onClick={() => toggleDisqualify(p1Obj.id)} 
                                                        className="btn-dq-icon" 
                                                        title={p1Obj.isDisqualified ? "Reintegrar jugador" : "Descalificar jugador"}
                                                    >
                                                        <UserX className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                            <input 
                                                type="number" 
                                                value={localScores[`${ri}_${mi}`]?.p1Score ?? m.p1Score} 
                                                onChange={e => handleLocalScoreChange(ri, mi, 'p1Score', e.target.value)} 
                                                disabled={role === 'guest' || m.isLocked} 
                                                className="bracket-score-input" 
                                            />
                                        </div>
                                        <div className="bracket-player-row bottom">
                                            <div className={`bracket-player-name ${m.status === 'completed' && Number(m.p2Score) > Number(m.p1Score) ? 'is-winner' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span>{p2Obj?.name || 'POR DEFINIR'}</span>
                                                {p2Obj?.isDisqualified && <span className="dq-badge">(DQ)</span>}
                                                {role === 'admin' && p2Obj && (
                                                    <button 
                                                        onClick={() => toggleDisqualify(p2Obj.id)} 
                                                        className="btn-dq-icon" 
                                                        title={p2Obj.isDisqualified ? "Reintegrar jugador" : "Descalificar jugador"}
                                                    >
                                                        <UserX className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                            <input 
                                                type="number" 
                                                value={localScores[`${ri}_${mi}`]?.p2Score ?? m.p2Score} 
                                                onChange={e => handleLocalScoreChange(ri, mi, 'p2Score', e.target.value)} 
                                                disabled={role === 'guest' || m.isLocked} 
                                                className="bracket-score-input" 
                                            />
                                        </div>
                                        {role === 'admin' && (
                                            <div className="bracket-match-footer">
                                                <button 
                                                    onClick={() => toggleBLock(ri, mi)}
                                                    className={`btn-bracket-lock ${m.isLocked ? 'is-locked' : 'is-unlocked'}`}
                                                >
                                                    {m.isLocked ? <><Unlock className="w-3 h-3"/> Modificar Resultado</> : <><Lock className="w-3 h-3"/> Introducir Resultado</>}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}